import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import type { AppState } from '../../../lib/store';
import { useRescueContext } from '../../lib/RescueContext';
import { US_STATES } from '../../lib/utils';
import {
  rescueGwaPostcardsQuery,
  rescueRequestGwaPostcardsMutation,
  RescueRequestGwaPostcardsData,
  RescueRequestGwaPostcardsInput,
} from '../../lib/rescueQueries';
import { trackGwaPostcardsOrderSubmitted } from '../../analytics';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import { PortalField } from '../../components/FormField/PortalField';
import { PortalInput, PortalTextarea } from '../../components/FormField/PortalInput';
import { PortalSelect } from '../../components/FormField/PortalSelect';
import { ToggleGroup } from '../../components/FormField/ToggleGroup';
import { FormGrid } from '../../components/FormField/FormGrid';
import Button from '../../components/Button/Button';
import styles from './GwaPostcards.module.scss';

// Cards sell in fixed packs; the backend enforces the same 100–500-in-packs-of-100 rule.
const QUANTITY_PRESETS = ['100', '200', '300', '400', '500'] as const;
type QuantityChoice = (typeof QUANTITY_PRESETS)[number];
const DEFAULT_QUANTITY: QuantityChoice = '100';

/** Pragmatic email shape check — backend remains authoritative. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface OrderPostcardsModalProps {
  open: boolean;
  onClose: () => void;
  promoCode: string;
  /** Front-face preview image (data URL) for the order summary strip; null while still rendering. */
  frontImage: string | null;
}

interface SuccessState {
  orderId: string;
  quantity: number;
}

export default function OrderPostcardsModal({ open, onClose, promoCode, frontImage }: OrderPostcardsModalProps) {
  const { orgName, shippingAddress } = useRescueContext();
  const session = useSelector((state: AppState) => state.session);
  const [requestPostcards] = useMutation<RescueRequestGwaPostcardsData, { input: RescueRequestGwaPostcardsInput }>(
    rescueRequestGwaPostcardsMutation,
    { refetchQueries: [{ query: rescueGwaPostcardsQuery }] },
  );

  // Same pre-fill rules as the device/chip order forms: only seed from the stored org address
  // when it's a clean US address whose state matches the <select> options.
  const validUSAddress = useMemo(() => {
    if (!shippingAddress) {
      return null;
    }
    if (shippingAddress.country !== 'US') {
      return null;
    }
    if (!shippingAddress.state || !US_STATES.includes(shippingAddress.state)) {
      return null;
    }
    return shippingAddress;
  }, [shippingAddress]);

  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState(session?.email ?? '');
  const [quantityChoice, setQuantityChoice] = useState<QuantityChoice>(DEFAULT_QUANTITY);
  const [street, setStreet] = useState(validUSAddress?.line1 ?? '');
  const [apt, setApt] = useState(validUSAddress?.line2 ?? '');
  const [city, setCity] = useState(validUSAddress?.city ?? '');
  const [usState, setUsState] = useState(validUSAddress?.state ?? '');
  const [zip, setZip] = useState(validUSAddress?.zip ?? '');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState<SuccessState | null>(null);

  // Once-per-open-cycle seeding + synchronous double-submit guard, mirroring OrderDevicesModal
  // (see the comments there for the accepted seed-overwrite race).
  const seededAddressRef = useRef(false);
  const seededEmailRef = useRef(false);
  const submittingRef = useRef(false);
  useEffect(() => {
    if (!open) {
      seededAddressRef.current = false;
      return;
    }
    if (validUSAddress && !seededAddressRef.current) {
      setStreet(validUSAddress.line1 ?? '');
      setApt(validUSAddress.line2 ?? '');
      setCity(validUSAddress.city ?? '');
      setUsState(validUSAddress.state ?? '');
      setZip(validUSAddress.zip ?? '');
      seededAddressRef.current = true;
    }
  }, [open, validUSAddress]);
  useEffect(() => {
    if (!open) {
      seededEmailRef.current = false;
      return;
    }
    if (session?.email && !seededEmailRef.current) {
      setContactEmail(session.email);
      seededEmailRef.current = true;
    }
  }, [open, session?.email]);

  function resetForm() {
    setContactName('');
    setContactEmail(session?.email ?? '');
    setQuantityChoice(DEFAULT_QUANTITY);
    setStreet(validUSAddress?.line1 ?? '');
    setApt(validUSAddress?.line2 ?? '');
    setCity(validUSAddress?.city ?? '');
    setUsState(validUSAddress?.state ?? '');
    setZip(validUSAddress?.zip ?? '');
    setNotes('');
    setShowErrors(false);
    setSubmitError(null);
    setSubmitting(false);
    setSuccess(null);
    submittingRef.current = false;
  }

  function handleClose() {
    if (submittingRef.current) {
      return;
    }
    resetForm();
    onClose();
  }

  const quantity = Number.parseInt(quantityChoice, 10);
  const trimmedEmail = contactEmail.trim();

  const errors = {
    contactName: contactName.trim() === '',
    contactEmailMissing: trimmedEmail === '',
    contactEmailInvalid: trimmedEmail !== '' && !EMAIL_RE.test(trimmedEmail),
    quantity: !Number.isFinite(quantity) || quantity <= 0,
    street: street.trim() === '',
    city: city.trim() === '',
    state: usState === '',
    zip: zip.trim() === '',
  };
  const hasErrors = Object.values(errors).some(Boolean);

  async function handleSubmit() {
    if (submittingRef.current) {
      return;
    }
    if (hasErrors) {
      setSubmitError(null);
      setShowErrors(true);
      return;
    }
    submittingRef.current = true;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const result = await requestPostcards({
        variables: {
          input: {
            quantity,
            contactName: contactName.trim(),
            contactEmail: trimmedEmail,
            shippingAddress: {
              line1: street.trim(),
              line2: apt.trim() ? apt.trim() : null,
              city: city.trim(),
              state: usState,
              // US-only — the form's state list is US states, so country is hardcoded.
              country: 'US',
              zip: zip.trim(),
            },
            notes: notes.trim() ? notes.trim() : null,
          },
        },
      });
      const order = result.data?.rescueRequestGwaPostcards;
      if (!order) {
        throw new Error('Order submission was not accepted');
      }
      trackGwaPostcardsOrderSubmitted(quantity);
      setSuccess({ orderId: order.id, quantity });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit order request');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  if (success) {
    const { orderId, quantity: confirmedQuantity } = success;
    return (
      <Modal open={open} onClose={handleClose}>
        <div className={styles.orderConfirmState}>
          <div className={styles.orderConfirmIcon} aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M5 12.5L10 17.5L19 7.5"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2 className={styles.orderConfirmTitle}>Request Submitted</h2>
          <p className={styles.orderConfirmDesc}>
            Your order for <strong>{confirmedQuantity} adoption cards</strong> has been submitted for approval. Once
            approved, they're printed with your rescue's QR code and promo code and shipped to your address.
          </p>
          <div className={styles.orderConfirmFooter}>
            <div className={styles.orderConfirmOrderNumber}>Order #{orderId}</div>
            <Button variant="green" onClick={handleClose}>
              Done
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={handleClose} size="wide">
      <ModalTitle>Order Adoption Cards</ModalTitle>
      <p className={styles.modalDescription}>
        Request printed adoption cards for <strong>{orgName || 'your rescue'}</strong>. Cards are personalized with your
        link and reviewed within 1 business day.
      </p>

      <div className={styles.orderPreview}>
        {frontImage && (
          <div className={styles.orderPreviewThumb}>
            <img src={frontImage} alt="Adoption card front" />
          </div>
        )}
        <div>
          <div className={styles.orderPreviewTitle}>Fi Mini adoption card — {promoCode}</div>
          <div className={styles.orderPreviewSub}>4.25×6 double-sided · your QR + code {promoCode}</div>
        </div>
        <div className={styles.orderPreviewCost}>
          <div className={styles.orderPreviewCostValue}>Free</div>
          <div className={styles.orderPreviewCostNote}>to your rescue</div>
        </div>
      </div>

      <PortalField
        label="Number of Cards *"
        hint="Cards ship in packs of 100, from 100 up to 500 per order. Need more? Add a note below and we'll follow up."
      >
        <ToggleGroup
          options={QUANTITY_PRESETS.map((q) => ({ value: q, label: q }))}
          value={quantityChoice}
          onChange={(v) => setQuantityChoice(v as QuantityChoice)}
        />
      </PortalField>

      <div className={styles.orderSectionHeading}>Ship To</div>
      <FormGrid>
        <PortalField
          label="Contact Name *"
          error="Please provide a contact name"
          showError={showErrors && errors.contactName}
        >
          <PortalInput
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="Full name"
            hasError={showErrors && errors.contactName}
          />
        </PortalField>
        <PortalField
          label="Contact Email *"
          error={errors.contactEmailMissing ? 'Please provide a contact email' : 'Please provide a valid email address'}
          showError={showErrors && (errors.contactEmailMissing || errors.contactEmailInvalid)}
        >
          <PortalInput
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="you@example.org"
            hasError={showErrors && (errors.contactEmailMissing || errors.contactEmailInvalid)}
          />
        </PortalField>
        <PortalField
          label="Street Address *"
          fullWidth
          error="Please provide a street address"
          showError={showErrors && errors.street}
        >
          <PortalInput
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            placeholder="123 Main Street"
            hasError={showErrors && errors.street}
          />
        </PortalField>
        <PortalField label="Apt / Suite">
          <PortalInput value={apt} onChange={(e) => setApt(e.target.value)} placeholder="Apt / Suite" />
        </PortalField>
        <PortalField label="City *" error="Please provide a city" showError={showErrors && errors.city}>
          <PortalInput
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="City"
            hasError={showErrors && errors.city}
          />
        </PortalField>
        <PortalField label="State *" error="Please select a state" showError={showErrors && errors.state}>
          <PortalSelect
            value={usState}
            onChange={(e) => setUsState(e.target.value)}
            hasError={showErrors && errors.state}
          >
            <option value="">Select…</option>
            {US_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </PortalSelect>
        </PortalField>
        <PortalField label="Zip Code *" error="Please provide a zip code" showError={showErrors && errors.zip}>
          <PortalInput
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            placeholder="ZIP"
            inputMode="numeric"
            hasError={showErrors && errors.zip}
          />
        </PortalField>
      </FormGrid>

      <PortalField label="Notes (optional)">
        <PortalTextarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. upcoming adoption event, deadline, quantity over 500"
        />
      </PortalField>

      {submitError && (
        <div className={styles.submitErrorBanner} role="alert">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
            <path d="M12 7.5V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <circle cx="12" cy="16.5" r="1.25" fill="currentColor" />
          </svg>
          <span>{submitError}</span>
        </div>
      )}

      <ModalActions>
        <Button variant="secondary" onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit Request'}
        </Button>
      </ModalActions>
    </Modal>
  );
}
